import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Check, X, Upload, Search, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, formatPrice } from "../../lib/api";

const inputCls =
  "border border-brand-line bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-ink rounded-md w-full";

const emptyDraft = { phone: "", percent: "", card_number: "", name: "",
                     total_spent: "", birth_date: "", is_active: true };

const AdminDiscounts = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editing, setEditing] = useState(null); // {id, ...}
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(
    (q = "") =>
      api
        .get("/admin/discount-cards", { params: q ? { search: q } : {} })
        .then(({ data }) => setCards(data))
        .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  // Axtarışda hər hərfə sorğu getməsin.
  useEffect(() => {
    const timer = setTimeout(() => load(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search, load]);

  const create = async () => {
    try {
      await api.post("/admin/discount-cards", {
        ...draft,
        percent: Number(String(draft.percent).replace(",", ".")),
        total_spent: Number(String(draft.total_spent || 0).replace(",", ".")) || 0,
      });
      setAdding(false);
      setDraft(emptyDraft);
      toast.success("Kart əlavə edildi");
      load(search.trim());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/discount-cards/${editing.id}`, {
        phone: editing.phone,
        percent: Number(String(editing.percent).replace(",", ".")),
        card_number: editing.card_number || "",
        name: editing.name || "",
        total_spent: Number(String(editing.total_spent || 0).replace(",", ".")) || 0,
        birth_date: editing.birth_date || "",
        is_active: editing.is_active,
      });
      setEditing(null);
      toast.success("Yadda saxlanıldı");
      load(search.trim());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/discount-cards/${c.id}`, {
        phone: c.phone,
        percent: c.percent,
        card_number: c.card_number || "",
        name: c.name || "",
        total_spent: c.total_spent || 0,
        birth_date: c.birth_date || "",
        is_active: !c.is_active,
      });
      load(search.trim());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`${c.phone_display} nömrəli kart silinsin?`)) return;
    try {
      await api.delete(`/admin/discount-cards/${c.id}`);
      toast.success("Kart silindi");
      load(search.trim());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/discount-cards/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      toast.success(`${data.added} yeni, ${data.updated} yeniləndi`);
      load(search.trim());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-discounts-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl">
          Endirim kartları
          {!loading && <span className="text-brand-muted text-base ml-2">({cards.length})</span>}
        </h1>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={upload}
            className="hidden"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            data-testid="discount-import-btn"
            className="flex items-center gap-2 border border-brand-line px-4 py-2.5 text-sm rounded-md hover:bg-brand-bg2 disabled:opacity-50"
          >
            <Upload size={16} strokeWidth={1.5} /> {importing ? "Yüklənir..." : "Excel yüklə"}
          </button>
          <button
            onClick={() => { setAdding(true); setDraft(emptyDraft); }}
            data-testid="discount-add-btn"
            className="flex items-center gap-2 bg-brand-ink text-white px-4 py-2.5 text-sm rounded-md"
          >
            <Plus size={16} strokeWidth={1.5} /> Yeni kart
          </button>
        </div>
      </div>

      <p className="text-xs text-brand-muted mb-5 leading-relaxed">
        Kart telefon nömrəsinə bağlıdır. Müştəri sifariş verərkən nömrəsini yazanda endirim
        avtomatik tətbiq olunur. Nömrə <b>son 9 rəqəmə</b> görə tutuşdurulur — müştəri
        “0501234567”, “+994 50 123 45 67” və ya “502412211” yazsa da eyni kart tapılır.<br />
        Excel faylında başlıq sətri olmalıdır: <b>Telefon</b> və <b>Endirim %</b> (məcburi),
        <b> Ad</b>, <b>Soyad</b>, <b>Kart kodu</b>, <b>Bonus</b>, <b>Doğum tarixi</b> (istəyə bağlı).
        Eyni nömrə varsa yenilənir, dublikat yaranmır.
      </p>

      {report && (
        <div className="mb-5 border border-brand-line rounded-md p-4 text-sm bg-white" data-testid="discount-import-report">
          <p>
            <b>{report.added}</b> yeni kart, <b>{report.updated}</b> yeniləndi
            {report.skipped > 0 && <>, <b className="text-destructive">{report.skipped}</b> sətir atlanıldı</>}
          </p>
          {report.skipped_rows?.length > 0 && (
            <ul className="mt-2 text-xs text-brand-muted space-y-0.5 max-h-40 overflow-y-auto">
              {report.skipped_rows.map((r, i) => (
                <li key={i}>{r.row}-cü sətir{r.name ? ` · ${r.name}` : ""} — {r.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="relative mb-5">
        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Nömrə, ad və ya kart nömrəsi ilə axtar"
          data-testid="discount-search"
          className={`${inputCls} pl-9`}
        />
      </div>

      {adding && (
        <div className="mb-5 border border-brand-line rounded-md p-4 bg-white grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="discount-add-form">
          <input className={inputCls} placeholder="Telefon *" value={draft.phone}
                 onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
          <input className={inputCls} placeholder="Endirim % *" value={draft.percent}
                 onChange={(e) => setDraft({ ...draft, percent: e.target.value })} />
          <input className={inputCls} placeholder="Kart nömrəsi" value={draft.card_number}
                 onChange={(e) => setDraft({ ...draft, card_number: e.target.value })} />
          <input className={inputCls} placeholder="Müştərinin adı" value={draft.name}
                 onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <input className={inputCls} placeholder="Cəmi alış (₼)" value={draft.total_spent}
                 onChange={(e) => setDraft({ ...draft, total_spent: e.target.value })} />
          <input className={inputCls} placeholder="Doğum tarixi" value={draft.birth_date}
                 onChange={(e) => setDraft({ ...draft, birth_date: e.target.value })} />
          <div className="sm:col-span-4 flex gap-2">
            <button onClick={create} data-testid="discount-save-btn"
                    className="flex items-center gap-2 bg-brand-ink text-white px-4 py-2.5 text-sm rounded-md">
              <Check size={16} strokeWidth={1.5} /> Yadda saxla
            </button>
            <button onClick={() => setAdding(false)}
                    className="flex items-center gap-2 border border-brand-line px-4 py-2.5 text-sm rounded-md">
              <X size={16} strokeWidth={1.5} /> İmtina
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-brand-muted py-10 text-center">Yüklənir...</p>
      ) : cards.length === 0 ? (
        <p className="text-brand-muted py-10 text-center">Kart yoxdur</p>
      ) : (
        <div className="space-y-2">
          {cards.map((c) =>
            editing?.id === c.id ? (
              <div key={c.id} className="border border-brand-ink rounded-md p-4 bg-white grid grid-cols-1 sm:grid-cols-4 gap-3">
                <input className={inputCls} value={editing.phone}
                       onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
                <input className={inputCls} value={editing.percent}
                       onChange={(e) => setEditing({ ...editing, percent: e.target.value })} />
                <input className={inputCls} value={editing.card_number || ""}
                       onChange={(e) => setEditing({ ...editing, card_number: e.target.value })} />
                <input className={inputCls} value={editing.name || ""}
                       onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                <input className={inputCls} placeholder="Cəmi alış (₼)" value={editing.total_spent ?? ""}
                       onChange={(e) => setEditing({ ...editing, total_spent: e.target.value })} />
                <input className={inputCls} placeholder="Doğum tarixi" value={editing.birth_date || ""}
                       onChange={(e) => setEditing({ ...editing, birth_date: e.target.value })} />
                <div className="sm:col-span-4 flex gap-2">
                  <button onClick={saveEdit} className="flex items-center gap-2 bg-brand-ink text-white px-4 py-2.5 text-sm rounded-md">
                    <Check size={16} strokeWidth={1.5} /> Saxla
                  </button>
                  <button onClick={() => setEditing(null)} className="flex items-center gap-2 border border-brand-line px-4 py-2.5 text-sm rounded-md">
                    <X size={16} strokeWidth={1.5} /> İmtina
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={c.id}
                data-testid={`discount-card-${c.phone}`}
                className={`border border-brand-line rounded-md p-4 bg-white flex flex-wrap items-center gap-3 justify-between ${c.is_active ? "" : "opacity-50"}`}
              >
                <div className="min-w-0">
                  <p className="font-mono text-sm">{c.phone_display || c.phone}</p>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {c.name || "—"}{c.card_number ? ` · kart ${c.card_number}` : ""}
                  </p>
                  {c.total_spent > 0 && (
                    <p className="text-xs text-brand-sub mt-0.5">
                      Cəmi alış: {formatPrice(c.total_spent)}
                      {c.birth_date ? ` · doğum ${c.birth_date}` : ""}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-serif text-xl">{c.percent}%</span>
                  <button onClick={() => toggleActive(c)} title={c.is_active ? "Söndür" : "Aktivləşdir"}
                          className="p-2.5 border border-brand-line rounded-md hover:bg-brand-bg2">
                    {c.is_active
                      ? <Eye size={16} strokeWidth={1.5} />
                      : <EyeOff size={16} strokeWidth={1.5} />}
                  </button>
                  <button onClick={() => setEditing({ ...c, phone: c.phone_display || c.phone })}
                          className="p-2.5 border border-brand-line rounded-md hover:bg-brand-bg2">
                    <Pencil size={16} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(c)}
                          className="p-2.5 border border-brand-line rounded-md hover:bg-brand-bg2 text-destructive">
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDiscounts;
